import { useState, useEffect } from 'react'
import { X } from 'lucide-react'
import { supabase } from '../services/supabase'
import Button from './Button'

interface Usuario {
    id: string
    user_id: string
    nome: string
    email: string
    telefone: string
    cep: string | null
    endereco: string | null
    cidade: string | null
    estado: string | null
    created_at: string
}

interface ModalUsuarioProps {
    isOpen: boolean
    onClose: () => void
    onSuccess: () => void
    usuario?: Usuario | null
    onShowToast: (type: 'success' | 'error', title: string, message?: string) => void
}

export default function ModalUsuario({
    isOpen,
    onClose,
    onSuccess,
    usuario,
    onShowToast
}: ModalUsuarioProps) {
    const [loading, setLoading] = useState(false)
    const [formData, setFormData] = useState({
        nome: '',
        email: '',
        telefone: '',
        cep: '',
        endereco: '',
        cidade: '',
        estado: ''
    })

    useEffect(() => {
        if (usuario) {
            setFormData({
                nome: usuario.nome,
                email: usuario.email,
                telefone: usuario.telefone,
                cep: usuario.cep || '',
                endereco: usuario.endereco || '',
                cidade: usuario.cidade || '',
                estado: usuario.estado || ''
            })
        } else {
            setFormData({
                nome: '',
                email: '',
                telefone: '',
                cep: '',
                endereco: '',
                cidade: '',
                estado: ''
            })
        }
    }, [usuario, isOpen])

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target
        setFormData(prev => ({
            ...prev,
            [name]: value
        }))
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setLoading(true)

        try {
            if (usuario) {
                // Atualizar usuário existente
                const { error } = await supabase
                    .from('corretores')
                    .update({
                        nome: formData.nome,
                        email: formData.email,
                        telefone: formData.telefone,
                        cep: formData.cep || null,
                        endereco: formData.endereco || null,
                        cidade: formData.cidade || null,
                        estado: formData.estado || null,
                        updated_at: new Date().toISOString()
                    })
                    .eq('id', usuario.id)

                if (error) throw error

                onShowToast('success', 'Usuário atualizado com sucesso!')
            } else {
                // Criar novo usuário
                // Primeiro, criar usuário na tabela users
                const { data: authData, error: authError } = await supabase.auth.admin.createUser({
                    email: formData.email,
                    password: '123456', // Senha padrão
                    email_confirm: true
                })

                if (authError) throw authError

                if (!authData.user) {
                    throw new Error('Erro ao criar usuário')
                }

                // Depois, criar corretor na tabela corretores
                const { error: corretorError } = await supabase
                    .from('corretores')
                    .insert({
                        user_id: authData.user.id,
                        nome: formData.nome,
                        email: formData.email,
                        telefone: formData.telefone,
                        cep: formData.cep || null,
                        endereco: formData.endereco || null,
                        cidade: formData.cidade || null,
                        estado: formData.estado || null
                    })

                if (corretorError) throw corretorError

                onShowToast('success', 'Usuário criado com sucesso!', 'Senha padrão: 123456')
            }

            onSuccess()
        } catch (error: any) {
            console.error('Erro ao salvar usuário:', error)
            onShowToast('error', 'Erro ao salvar usuário', error.message)
        } finally {
            setLoading(false)
        }
    }

    if (!isOpen) return null

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                        {usuario ? 'Editar Usuário' : 'Novo Usuário'}
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    >
                        <X className="h-6 w-6" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    {/* Nome */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Nome Completo *
                        </label>
                        <input
                            type="text"
                            name="nome"
                            value={formData.nome}
                            onChange={handleInputChange}
                            required
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            placeholder="Digite o nome completo"
                        />
                    </div>

                    {/* Email */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Email *
                        </label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            placeholder="Digite o email"
                        />
                    </div>

                    {/* Telefone */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Telefone *
                        </label>
                        <input
                            type="tel"
                            name="telefone"
                            value={formData.telefone}
                            onChange={handleInputChange}
                            required
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            placeholder="(11) 99999-9999"
                        />
                    </div>

                    {/* CEP */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            CEP
                        </label>
                        <input
                            type="text"
                            name="cep"
                            value={formData.cep}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            placeholder="00000-000"
                        />
                    </div>

                    {/* Endereço */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Endereço
                        </label>
                        <input
                            type="text"
                            name="endereco"
                            value={formData.endereco}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            placeholder="Rua, número, bairro"
                        />
                    </div>

                    {/* Cidade e Estado */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Cidade
                            </label>
                            <input
                                type="text"
                                name="cidade"
                                value={formData.cidade}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                placeholder="Digite a cidade"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Estado
                            </label>
                            <select
                                name="estado"
                                value={formData.estado}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            >
                                <option value="">Selecione o estado</option>
                                <option value="AC">Acre</option>
                                <option value="AL">Alagoas</option>
                                <option value="AP">Amapá</option>
                                <option value="AM">Amazonas</option>
                                <option value="BA">Bahia</option>
                                <option value="CE">Ceará</option>
                                <option value="DF">Distrito Federal</option>
                                <option value="ES">Espírito Santo</option>
                                <option value="GO">Goiás</option>
                                <option value="MA">Maranhão</option>
                                <option value="MT">Mato Grosso</option>
                                <option value="MS">Mato Grosso do Sul</option>
                                <option value="MG">Minas Gerais</option>
                                <option value="PA">Pará</option>
                                <option value="PB">Paraíba</option>
                                <option value="PR">Paraná</option>
                                <option value="PE">Pernambuco</option>
                                <option value="PI">Piauí</option>
                                <option value="RJ">Rio de Janeiro</option>
                                <option value="RN">Rio Grande do Norte</option>
                                <option value="RS">Rio Grande do Sul</option>
                                <option value="RO">Rondônia</option>
                                <option value="RR">Roraima</option>
                                <option value="SC">Santa Catarina</option>
                                <option value="SP">São Paulo</option>
                                <option value="SE">Sergipe</option>
                                <option value="TO">Tocantins</option>
                            </select>
                        </div>
                    </div>

                    {/* Botões */}
                    <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <Button
                            type="button"
                            variant="btn_secondary"
                            onClick={onClose}
                            className="flex-1"
                            disabled={loading}
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            variant="btn_add"
                            className="flex-1"
                            loading={loading}
                        >
                            {usuario ? 'Atualizar' : 'Criar'} Usuário
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    )
}

